// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
// import config from './config'; // config might not be used directly anymore
// import graphic from './graphic'; // graphic module not used, draw is in-file

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const pTime = d3.timeParse('%Y-%m-%d');
const HISTORICAL_DATA_MONTHS_TO_SHOW = 12; // Number of past months to display for historical data
const SERIES_COLOR = '#53a796'; // Both maturities share one color; only one is ever on screen
// A zero baseline flattens a year of yield movement into the top sixth of the
// plot, so the axis stays fitted to the data. Flip to true to compare.
const ZERO_BASELINE = false;

const MOTION_OK = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

// Frozen mode: ?freeze=true&date=20260914 pins the graphic to the bot's daily
// close for that date and stops the live polling, so a story keeps the numbers
// it published with. Without a date it freezes at the bot's latest close.
// Read off the graphic's own URL, which is where pym appends its parameters too.
function getQueryParam(name) {
  const match = new RegExp(`[?&]${name}(?:=([^&#]*))?(?:[&#]|$)`).exec(window.location.search);
  if (!match) return null;
  return match[1] === undefined ? '' : decodeURIComponent(match[1].replace(/\+/g, ' '));
}

function parseFreezeDate(raw) {
  if (!raw) return null; // Frozen, but at whatever the bot published most recently
  const digits = raw.replace(/[^0-9]/g, ''); // Accepts 20260914 or 2026-09-14
  if (digits.length !== 8) {
    console.warn(`Ignoring unreadable freeze date "${raw}"; expected YYYYMMDD.`);
    return null;
  }
  const iso = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  const parsed = pTime(iso);
  // Round-trip the parse: month 13 or Feb. 30 would otherwise roll over into a
  // real but wrong date rather than failing.
  if (!parsed || d3.timeFormat('%Y-%m-%d')(parsed) !== iso) {
    console.warn(`Ignoring invalid freeze date "${raw}".`);
    return null;
  }
  return parsed;
}

const freezeParam = getQueryParam('freeze');
const FROZEN = freezeParam === '' || freezeParam === 'true' || freezeParam === '1';
const FREEZE_DATE = FROZEN ? parseFreezeDate(getQueryParam('date')) : null;

// Series configuration
const seriesConfig = {
  '10Y': {
    label: '10-Year',
    fullName: '10-Year Treasury Yield',
    cnnId: 'US10YY-TU1',
    historicalDataUrl: 'https://ix.cnn.io/data/treasury-yields/treasury_yield_10y.json',
    historicalData: [],
    currentRealTimeData: null,
    lastHistoricalPoint: null,
    feedCurrentData: null,
    color: SERIES_COLOR,
    dataValueKey: 'value',
  },
  '30Y': {
    label: '30-Year',
    fullName: '30-Year Treasury Yield',
    cnnId: 'US30YY-TU1',
    historicalDataUrl: 'https://ix.cnn.io/data/treasury-yields/treasury_yield_30y.json',
    historicalData: [],
    currentRealTimeData: null,
    lastHistoricalPoint: null,
    feedCurrentData: null,
    color: SERIES_COLOR,
    dataValueKey: 'value',
  },
};

// Event callouts, drawn only when the date falls inside the visible window.
// Shape: { dateStr: '2026-09-10', text: 'Sept. 10: What happened',
//          boldPrefix: 'Sept. 10:', textYOffset: -180, lineStartOffsetY: 20,
//          textWrapWidth: 120 }
// textYOffset is measured from the line, so negative sits the label above it.
const customAnnotations = [];

let activeSeriesKey = '10Y'; // Default active series

// Globals that will be updated based on activeSeriesKey
let activeChartData;
let activeCurrentRealTimeData;
let minDomain; let maxDomain; let minValue; let
  maxValue;

const margins = {
  top: 20,
  right: 120, // Room for the value and timestamp at the end of the line
  bottom: 25,
  left: 40,
};

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) node.innerHTML = meta[id];
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }

  stylePremium(false, false); // Assuming CNNPolitics, CNNBusiness are false

  loadAllHistoricalData(); // Start data loading process
  window.onresize = prepareChartDataAndDraw; // Redraw on resize
};

function formatAmPm(text) {
  return text.replace(/AM/g, 'a.m.').replace(/PM/g, 'p.m.');
}

// Simplified for Treasury yields, always shows time from 'last_updated'
function formatEasternTime(date) {
  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  };
  const timeString = date.toLocaleTimeString('en-US', options);
  return formatAmPm(`${timeString} ET`);
}

const AP_MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

// Month and day in Eastern time, so the endpoint label can't disagree with the
// timestamp beside it for readers in other time zones.
function formatEasternDate(date) {
  const [month, day] = date
    .toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'America/New_York' })
    .split('/')
    .map(Number);
  return `${AP_MONTHS[month - 1]} ${day}, ${date.getFullYear()}`;
}

function formatFootnoteDateTime(date) {
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  };
  const dateTimeString = date.toLocaleString('en-US', options);
  return formatAmPm(`${dateTimeString} ET`);
}

const STANDING_NOTES = 'Yields rise when bond prices fall, and fall when prices rise. One basis point equals 0.01 percentage point.';

function updateFootnote(point) {
  const footnoteElement = document.querySelector('.footnote');
  if (!footnoteElement) return;
  if (!point || !point.date) {
    footnoteElement.textContent = `Notes: Update time unavailable. ${STANDING_NOTES}`;
    return;
  }
  // A daily close is a calendar date rather than an instant, so it's formatted
  // without the time zone conversion that would shift it for overseas readers.
  const stamp = point.dateOnly
    ? `Data as of ${d3.timeFormat('%B %-d, %Y')(point.date)}`
    : `Data updated ${formatFootnoteDateTime(point.date)}`;
  footnoteElement.textContent = `Notes: ${stamp}. ${STANDING_NOTES}`;
}

// The most recent value for a series: live market data if we have it, otherwise
// the daily close published with the historical feed.
function getLatestPoint(series) {
  if (!series) return null;
  return series.currentRealTimeData || series.feedCurrentData || series.lastHistoricalPoint || null;
}

function updateDynamicSubhead(activeKey) {
  const subhedElement = document.querySelector('.subhed.dek');
  if (!subhedElement) return;

  const series = seriesConfig[activeKey];
  const latest = getLatestPoint(series);
  if (!latest || !series.historicalData.length) return;

  // Compare against the oldest point still on the chart, so the dek stays true
  // as the 12-month window rolls forward.
  const baseline = series.historicalData[0];
  const change = latest.value - baseline.value;

  const basisPoints = Math.round(Math.abs(change) * 100);

  const seriesName = `<span class="dek-key" style="text-decoration-color: ${series.color};">${series.label.toLowerCase()} Treasury yield</span>`;
  const currentLevel = `<b>${d3.format('.2f')(latest.value)}%</b>`;

  // A frozen graphic describes a past moment, so the whole sentence shifts
  // tense: "a year ago" is relative to now, "a year earlier" to the freeze date.
  const verb = FROZEN ? 'was' : 'is';
  const baselineLabel = FROZEN ? 'a year earlier' : 'a year ago';

  let comparison;
  if (basisPoints < 5) {
    comparison = `little changed from ${baselineLabel}`;
  } else {
    comparison = `${change > 0 ? 'up' : 'down'} <b>${basisPoints} basis points</b> from ${baselineLabel}`;
  }

  subhedElement.innerHTML = `The ${seriesName} ${verb} ${currentLevel}, ${comparison}.`;

  if (process.env.NODE_ENV === 'development') {
    console.log(`Dek math — ${series.label}: ${latest.value.toFixed(2)}% now vs. ${baseline.value.toFixed(2)}% on ${d3.timeFormat('%B %-d, %Y')(baseline.date)}, a change of ${(change * 100).toFixed(0)} basis points`);
  }

  if (window.pymChild) window.pymChild.sendHeight(); // Recalculate height after subhead update
}

async function loadAllHistoricalData() {
  const promises = Object.keys(seriesConfig).map((key) => {
    const series = seriesConfig[key];
    return d3.json(series.historicalDataUrl)
      .then((apiResponse) => {
        // Assuming historical data is in apiResponse.data based on user's sample
        if (apiResponse && apiResponse.data) {
          series.historicalData = apiResponse.data.map(d => ({
            date: pTime(d.date),
            value: +d[series.dataValueKey],
          }));

          // The window ends at the freeze date rather than today, so a frozen
          // graphic doesn't drift right with empty space as the months roll on.
          // A freeze date past the end of the feed clamps to the last session,
          // which is what happens when a story publishes before the bot runs.
          const lastAvailable = series.historicalData.length
            ? series.historicalData[series.historicalData.length - 1].date
            : null;
          let windowEnd = FREEZE_DATE || new Date();
          if (FREEZE_DATE && lastAvailable && FREEZE_DATE > lastAvailable) {
            windowEnd = lastAvailable;
          }

          const timeFilter = new Date(windowEnd.getTime());
          timeFilter.setMonth(timeFilter.getMonth() - HISTORICAL_DATA_MONTHS_TO_SHOW);
          series.historicalData = series.historicalData.filter(
            d => d.date > timeFilter && (!FREEZE_DATE || d.date <= windowEnd),
          );

          if (series.historicalData.length > 0) {
            series.lastHistoricalPoint = series.historicalData[series.historicalData.length - 1];
          }

          if (FROZEN) {
            // Freeze on the bot's close for that day. Markets are shut on
            // weekends and holidays, so this lands on the last session at or
            // before the requested date.
            series.feedCurrentData = series.lastHistoricalPoint
              ? {
                date: series.lastHistoricalPoint.date,
                value: series.lastHistoricalPoint.value,
                dateOnly: true, // A daily close carries no time of day
              }
              : null;
          } else if (apiResponse.current_yield != null) {
            // The feed ships its own latest close and timestamp. Keep it as a
            // fallback so the dek, end label and note still fill in if the
            // real-time markets endpoint is unavailable.
            series.feedCurrentData = {
              date: apiResponse.data_last_updated_iso
                ? new Date(apiResponse.data_last_updated_iso)
                : series.lastHistoricalPoint && series.lastHistoricalPoint.date,
              value: +apiResponse.current_yield,
              prevValue: +apiResponse.previous_yield,
              change: +apiResponse.price_change,
              percentChange: +apiResponse.percent_change / 100,
            };
          }
        } else {
          console.warn(`No data property in response for ${series.label} from ${series.historicalDataUrl}`);
          series.historicalData = []; // Ensure it's an empty array
        }
      })
      .catch((error) => {
        console.error(`Failed to load historical data for ${series.label}:`, error);
        series.historicalData = []; // Ensure it's an empty array on error
      });
  });

  await Promise.all(promises);

  createToggleButtons();
  prepareChartDataAndDraw(); // Initial draw
  updateDynamicSubhead(activeSeriesKey); // Initial subhead update

  const initialLatest = getLatestPoint(seriesConfig[activeSeriesKey]);
  if (initialLatest) updateFootnote(initialLatest);

  if (FROZEN) {
    const frozenAt = initialLatest && d3.timeFormat('%Y-%m-%d')(initialLatest.date);
    const requested = FREEZE_DATE && d3.timeFormat('%Y-%m-%d')(FREEZE_DATE);
    console.log(`Frozen graphic: showing the close for ${frozenAt || 'no available date'}${requested && requested !== frozenAt ? ` (nearest session at or before ${requested})` : ''}. Live updates are off.`);
    return;
  }

  fetchMarketData(); // Fetch initial real-time data
  setInterval(fetchMarketData, 30000); // Update every 30 seconds
}

async function fetchMarketData() {
  const cnnSummaryUrl = 'https://production.dataviz.cnn.io/markets/rates/summary/US03M-TU1,US02YY-TU1,US05YY-TU1,US10YY-TU1,US30YY-TU1';
  try {
    const response = await fetch(cnnSummaryUrl);
    const summaryDataArray = await response.json();
    let dataUpdatedForActiveSeries = false;

    Object.keys(seriesConfig).forEach((key) => {
      const series = seriesConfig[key];
      const seriesRealTimeData = summaryDataArray.find(item => item.symbol === series.cnnId);

      if (seriesRealTimeData) {
        const newRealTimeData = {
          date: new Date(seriesRealTimeData.last_updated),
          value: +seriesRealTimeData.summary_yield,
          prevValue: +seriesRealTimeData.prev_summary_yield,
          change: +seriesRealTimeData.price_change_from_prev_close,
          percentChange: parseFloat(seriesRealTimeData.percent_change_from_prev_close),
        };

        const oldTimestamp = series.currentRealTimeData ? series.currentRealTimeData.date.getTime() : 0;
        if (newRealTimeData.date.getTime() > oldTimestamp) {
          series.currentRealTimeData = newRealTimeData;
          if (key === activeSeriesKey) {
            dataUpdatedForActiveSeries = true;
          }
        }
      }
    });

    if (dataUpdatedForActiveSeries && seriesConfig[activeSeriesKey].currentRealTimeData) {
      updateFootnote(seriesConfig[activeSeriesKey].currentRealTimeData);
      prepareChartDataAndDraw(); // Redraw if active series data updated
      updateDynamicSubhead(activeSeriesKey); // Update subhead after data update
    } else if (!seriesConfig[activeSeriesKey].currentRealTimeData) {
      // Fallback footnote if no real-time data yet, but historical exists
      const latest = getLatestPoint(seriesConfig[activeSeriesKey]);
      if (latest) updateFootnote(latest);
    }
  } catch (error) {
    console.error('Failed to fetch market summary data:', error);
  }
}

function createToggleButtons() {
  let container = d3.select('#controls-container');
  if (container.empty()) {
    container = d3.select('#graphic').insert('div', ':first-child')
      .attr('id', 'controls-container')
      .attr('class', 'controls'); // Simple class for styling
  }

  Object.keys(seriesConfig).forEach((key) => {
    container.append('button')
      .attr('id', `btn-${key}`)
      .attr('class', key === activeSeriesKey ? 'active' : null)
      .text(seriesConfig[key].label)
      .on('click', () => {
        activeSeriesKey = key;
        updateButtonStyles();
        prepareChartDataAndDraw();
        updateDynamicSubhead(activeSeriesKey); // Update subhead on toggle
        // Update footnote immediately with potentially new active series data
        const latest = getLatestPoint(seriesConfig[activeSeriesKey]);
        if (latest) updateFootnote(latest);
      });
  });
}

function updateButtonStyles() {
  Object.keys(seriesConfig).forEach((key) => {
    d3.select(`#btn-${key}`).classed('active', key === activeSeriesKey);
  });
}

function prepareChartDataAndDraw() {
  const series = seriesConfig[activeSeriesKey];

  // Determine minValue and maxValue for the Y-axis scale based on the ACTIVE series
  let activeSeriesMinY; let
    activeSeriesMaxY;
  const latestPoint = getLatestPoint(series);
  if (series && series.historicalData && series.historicalData.length > 0) {
    const activeValues = series.historicalData.map(d => d.value);
    if (latestPoint && latestPoint.value != null) {
      activeValues.push(latestPoint.value);
    }
    if (activeValues.length > 0) {
      activeSeriesMinY = d3.min(activeValues);
      activeSeriesMaxY = d3.max(activeValues);
    } else {
      activeSeriesMinY = 0; activeSeriesMaxY = 5; // Fallback for active series
    }
  } else {
    activeSeriesMinY = 0; activeSeriesMaxY = 5; // Fallback if no active series data
  }

  const dataRangeForPadding = activeSeriesMaxY - activeSeriesMinY;
  const padding = (dataRangeForPadding === 0 || isNaN(dataRangeForPadding))
    ? 0.5
    : dataRangeForPadding * 0.1;

  minValue = ZERO_BASELINE ? 0 : activeSeriesMinY - padding;
  maxValue = activeSeriesMaxY + padding;

  // Logic to include 0 in the scale (operates on active series derived minValue)
  if (!ZERO_BASELINE) {
    if (minValue > 0 && minValue < 1) {
      minValue = 0;
    } else {
      minValue = Math.max(0, minValue);
    }
  }
  if (maxValue === minValue || isNaN(maxValue) || isNaN(minValue)) {
    maxValue = minValue + 1;
  }
  if (isNaN(minValue)) minValue = 0;
  if (isNaN(maxValue)) maxValue = minValue + 1;

  // Active series data for X-axis and line drawing
  activeChartData = series ? series.historicalData : [];
  activeCurrentRealTimeData = latestPoint;

  if (!series || !activeChartData || activeChartData.length === 0) {
    const graphicContainer = d3.select('#graphic');
    const controls = graphicContainer.select('#controls-container');
    graphicContainer.html('');
    if (!controls.empty()) {
      graphicContainer.node().prepend(controls.node());
    }
    graphicContainer.append('p').text(`Data for ${series ? series.label : 'selected series'} is currently unavailable or still loading.`);
    window.pymChild.sendHeight();
    return;
  }

  // X-Axis domain setup (based on active series)
  const allDates = activeChartData.map(d => d.date);
  if (activeCurrentRealTimeData && activeCurrentRealTimeData.date) {
    if (d3.max(allDates) < activeCurrentRealTimeData.date) {
      allDates.push(activeCurrentRealTimeData.date);
    }
  }
  minDomain = d3.min(activeChartData, d => d.date);
  maxDomain = d3.max(allDates);

  draw();
}

// Helper function for text wrapping (define it before it's used in draw)
function wrapText(textElements, width) {
  textElements.each(function () {
    const text = d3.select(this);
    const words = text.text().split(/\s+/).reverse();
    let word;
    let line = [];
    let lineNumber = 0;
    const lineHeight = 1.1; // ems
    const x = text.attr('x') || 0;
    const y = text.attr('y') || 0;
    const dy = parseFloat(text.attr('dy')) || 0;
    text.text(null);
    let tspan = text.append('tspan').attr('x', x).attr('y', y).attr('dy', `${dy}em`);

    while (word = words.pop()) {
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node().getComputedTextLength() > width && line.length > 1) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = text.append('tspan').attr('x', x).attr('y', y).attr('dy', `${++lineNumber * lineHeight + dy}em`)
          .text(word);
      }
    }
  });
}

function draw() {
  const container = d3.select('#graphic');
  // Clear only the SVG part, leave controls if they are children of #graphic
  container.select('svg').remove();
  container.select('p').remove(); // Remove any "loading/unavailable" messages

  let linePathData; // Declare linePathData here

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 5; // Adjusted for yields, maybe fewer ticks

  const currentSeries = seriesConfig[activeSeriesKey];

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .nice()
    .range([chartHeight, 0]);

  // Determine the number of Y ticks - aiming for consistency
  const numYTicks = isMobile ? 6 : 6; // 4 ticks for mobile, 5 for desktop (or your preferred numbers)

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i, nodes) => {
      if (HISTORICAL_DATA_MONTHS_TO_SHOW <= 12) {
        if (isMobile) {
          // For 12 months or less on mobile: only show first and last
          if (i === 0 || i === nodes.length - 1) {
            return d3.timeFormat('%b %Y')(d); // "Jan 2024" for first/last
          }
          return ''; // Hide intermediate labels
        }
        // For 12 months or less on desktop:
        return d3.timeFormat('%B %Y')(d); // "January 2024" for all ticks
      }
      // Original logic for longer time spans (> 12 months)
      if (isMobile) {
        if (i === 0 || i === nodes.length - 1) {
          return d3.timeFormat('%Y')(d);
        }
        return ''; // Hide intermediate labels
      }
      if (i === 0 || d3.timeFormat('%m')(d) === '01') {
        return d3.timeFormat('%Y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  // Precision follows the tick step, so a step of 0.2 labels 4.0 rather than
  // 4.00 and a zero-baselined axis labels whole points.
  const yTickValues = yScale.ticks(numYTicks);
  const yTickStep = yTickValues.length > 1
    ? Math.abs(yTickValues[1] - yTickValues[0])
    : 1;
  let yDecimals = 0;
  if (yTickStep < 0.1) {
    yDecimals = 2;
  } else if (yTickStep < 1) {
    yDecimals = 1;
  }
  const topTick = yTickValues[yTickValues.length - 1];

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .tickValues(yTickValues)
    // Unit on the top tick only, per the graphics style guide
    .tickFormat(d => `${d3.format(`.${yDecimals}f`)(d)}${d === topTick ? '%' : ''}`)
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value)); // Use .value

  const areaPath = d3.area()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y0(chartHeight)
    .y1(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  d3.select('.grid.y .domain').remove(); // Keep this to remove the default axis path

  // Explicitly add a zero line if 0 is within the yScale domain
  const yDomain = yScale.domain();
  if (yDomain[0] <= 0 && yDomain[1] >= 0) {
    g.append('line')
      .attr('class', 'zero-line')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', yScale(0))
      .attr('y2', yScale(0))
      .attr('stroke', '#bcbcbc') // Darker than the gridlines: zero is a value, not a reference
      .attr('stroke-width', 1);
    // .attr('shape-rendering', 'crispEdges'); // Optional, if you want it super crisp
  }

  if (activeChartData && activeChartData.length > 0) {
    linePathData = [...activeChartData]; // Assign here, don't re-declare

    // Check if current real-time data exists, is valid, and is more recent
    if (activeCurrentRealTimeData
        && activeCurrentRealTimeData.date
        && activeCurrentRealTimeData.value != null) {
      const lastHistoricalPoint = activeChartData[activeChartData.length - 1];
      if (activeCurrentRealTimeData.date.getTime() > lastHistoricalPoint.date.getTime()) {
        // Append the current real-time point if it's newer than the last historical point
        linePathData.push({
          date: activeCurrentRealTimeData.date,
          value: activeCurrentRealTimeData.value,
        });
      }
      // Optional: Handle case where current data is an update for the same day as the last historical point
      // else if (activeCurrentRealTimeData.date.getTime() === lastHistoricalPoint.date.getTime() &&
      //            activeCurrentRealTimeData.value !== lastHistoricalPoint.value) {
      //   // Replace the last point in linePathData with the updated currentRealTimeData
      //   linePathData[linePathData.length - 1] = {
      //     date: activeCurrentRealTimeData.date,
      //     value: activeCurrentRealTimeData.value,
      //   };
      // }
    }

    g.append('path')
      .attr('class', 'area')
      .datum(linePathData)
      .attr('fill', currentSeries.color)
      .attr('fill-opacity', 0.15)
      .attr('d', areaPath);

    const line = g.append('g')
      .attr('class', 'line');

    line.append('path')
      .datum(linePathData) // Use the potentially extended data
      .attr('stroke', currentSeries.color)
      .attr('stroke-width', isMobile ? 2 : 2.25)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('fill', 'none')
      .attr('d', path);
  }


  if (activeCurrentRealTimeData && activeCurrentRealTimeData.date && activeCurrentRealTimeData.value != null) {
    const currentX = xScale(activeCurrentRealTimeData.date);
    const currentY = yScale(activeCurrentRealTimeData.value);

    // Ensure current point is within chart boundaries before drawing
    if (currentX >= 0 && currentX <= chartWidth && currentY >= 0 && currentY <= chartHeight) {
      const currentPoint = g.append('g')
        .attr('class', 'current-point')
        .attr('transform', `translate(${[currentX, currentY]})`);

      // No pulse on a frozen graphic: it would signal live data that isn't.
      if (MOTION_OK && !FROZEN) {
        const halo = currentPoint.append('circle')
          .attr('class', 'pulse')
          .attr('r', 5)
          .attr('fill', currentSeries.color);

        // SMIL rather than CSS keyframes: animating r is reliable across the
        // older mobile browsers this rig still supports.
        halo.append('animate')
          .attr('attributeName', 'r')
          .attr('values', '5;16')
          .attr('dur', '2s')
          .attr('repeatCount', 'indefinite');

        halo.append('animate')
          .attr('attributeName', 'fill-opacity')
          .attr('values', '0.5;0')
          .attr('dur', '2s')
          .attr('repeatCount', 'indefinite');
      }

      currentPoint.append('circle')
        .attr('r', '4')
        .attr('fill', currentSeries.color)
        .attr('stroke', 'white')
        .attr('stroke-width', '1.5');

      const annotation = g.append('g')
        .attr('class', 'annotation')
        .attr('transform', `translate(${[currentX, currentY]})`);

      annotation.append('text')
        .attr('dx', '10px')
        .attr('class', 'annotation-price')
        .text(`${d3.format('.2f')(activeCurrentRealTimeData.value)}%`);

      if (activeCurrentRealTimeData.dateOnly) {
        // A frozen close has no time of day, and the year matters on an old story
        annotation.append('text')
          .attr('dx', '10px')
          .attr('class', 'annotation-time')
          .text(d3.timeFormat('%b %-d, %Y')(activeCurrentRealTimeData.date))
          .attr('dy', '1.2em');
      } else {
        // Stacked on two lines: the full date and time won't fit the right margin
        // on one line at mobile widths.
        annotation.append('text')
          .attr('dx', '10px')
          .attr('class', 'annotation-time')
          .text(`${formatEasternDate(activeCurrentRealTimeData.date)}`)
          .attr('dy', '1.2em');

        annotation.append('text')
          .attr('dx', '10px')
          .attr('class', 'annotation-time')
          .text(formatEasternTime(activeCurrentRealTimeData.date))
          .attr('dy', '2.4em');
      }
    }
  }

  // Draw custom annotations
  customAnnotations.forEach((ann) => {
    const annotationDateObj = pTime(ann.dateStr);

    if (!annotationDateObj || annotationDateObj < minDomain || annotationDateObj > maxDomain || !linePathData || linePathData.length < 1) {
      return; // Skip if date is invalid, out of bounds, or not enough data
    }

    const bisectDate = d3.bisector(d => d.date).left;
    const index = bisectDate(linePathData, annotationDateObj, 1);
    const p0 = linePathData[index - 1];
    const p1 = linePathData[index];

    let interpolatedValue;

    if (p0 && p1) {
      if (Math.abs(annotationDateObj.getTime() - p0.date.getTime()) < 86400000) { // Same day check for p0
        interpolatedValue = p0.value;
      } else if (Math.abs(annotationDateObj.getTime() - p1.date.getTime()) < 86400000) { // Same day check for p1
        interpolatedValue = p1.value;
      } else {
        const t = (annotationDateObj.getTime() - p0.date.getTime()) / (p1.date.getTime() - p0.date.getTime());
        if (t >= 0 && t <= 1) {
          interpolatedValue = p0.value * (1 - t) + p1.value * t;
        } else { // Date is outside p0-p1 segment, use closest point
          interpolatedValue = (annotationDateObj - p0.date < p1.date - annotationDateObj) ? p0.value : p1.value;
        }
      }
    } else if (p0) { // Only p0 exists (annotation is at or after last point)
      interpolatedValue = p0.value;
    } else if (p1) { // Only p1 exists (annotation is at or before first point)
      interpolatedValue = p1.value;
    } else {
      return; // Not enough data points
    }

    if (interpolatedValue === undefined || isNaN(interpolatedValue)) {
      return; // Skip if interpolation failed
    }

    const xPos = xScale(annotationDateObj);
    const yPosOnTrendLine = yScale(interpolatedValue);

    // Ensure the point on trend line is within drawable area
    if (xPos < 0 || xPos > chartWidth || yPosOnTrendLine < 0 || yPosOnTrendLine > chartHeight) {
      return; // Skip if target point is outside chart boundaries
    }

    const textActualY = yPosOnTrendLine + ann.textYOffset;
    const lineStartY = textActualY + ann.lineStartOffsetY; // e.g. textY - 18, to start line above text baseline

    const customAnnGroup = g.append('g')
      .attr('class', 'custom-annotation-item')
      .attr('transform', `translate(${xPos}, 0)`);

    customAnnGroup.append('line')
      .attr('x1', 0)
      .attr('y1', lineStartY)
      .attr('x2', 0)
      .attr('y2', yPosOnTrendLine)
      .attr('stroke', '#555') // Dark gray line
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2');

    const textElement = customAnnGroup.append('text')
      .attr('x', 0)
      .attr('y', textActualY)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#262626')
      .text(ann.text)
      .call(wrapText, ann.textWrapWidth);

    // After wrapping, try to bold the prefix if it exists on the first line
    if (ann.boldPrefix) {
      textElement.selectAll('tspan').each(function (d, i) {
        if (i === 0) { // Only operate on the first tspan (first line)
          const tspan = d3.select(this);
          const currentText = tspan.text();
          if (currentText.startsWith(ann.boldPrefix)) {
            const restOfText = currentText.substring(ann.boldPrefix.length);
            tspan.text(ann.boldPrefix) // Set text to only the prefix
              .style('font-weight', 'bold'); // Apply bold style directly

            // Insert a new tspan for the rest of the text, if any
            if (restOfText.length > 0) {
              d3.select(this.parentNode) // Get the parent <text> element
                .insert('tspan', () => tspan.node().nextSibling) // Insert after the current tspan
                .attr('dx', '0.2em') // Add a small space after the bold part, adjust as needed
                .text(restOfText);
            }
          }
        }
      });
    }
  });

  window.pymChild.sendHeight();
}
